import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Search, Sparkles } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import V2Shell from './V2Shell';

// Customs (HS) codes, merged from HS Codex (hsy): classify a product, browse the tables.
const API = 'api/replai/v2/hs/';
const SYSTEMS = [['EU', 'EU TARIC'], ['US', 'US HTS'], ['INT', 'International']];

const Chip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={`rounded-md px-3 py-1.5 text-sm ${active ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--line-2)]'}`}>
    {children}
  </button>
);

const CodeCard = ({ title, code, description, valid, corrected, rate, rateSource, special }) => (
  <div className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4">
    <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-3)]">{title}</div>
    <div className="num mt-1 text-2xl font-semibold">{code || '–'}</div>
    <div className="mt-1 text-xs">
      {valid ? <span className="text-green-700">Checked against the official table</span>
        : <span className="text-amber-700">Not found in the table: AI suggestion only</span>}
      {corrected && <span className="text-[var(--ink-3)]"> · corrected from {corrected}</span>}
    </div>
    {description && <p className="mt-2 text-sm text-[var(--ink-2)]">{description}</p>}
    {rate !== undefined && (
      <div className="mt-2 text-sm">US duty (General): <b>{rate || '–'}</b>
        <span className="text-xs text-[var(--ink-3)]"> · {rateSource === 'usitc_database' ? 'from USITC' : 'AI estimate'}</span>
        {special && <div className="text-xs text-[var(--ink-3)]">Special: {special}</div>}
      </div>
    )}
  </div>
);

const Classifier = () => {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [r, setR] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (picked || q.trim().length < 3) { setHits([]); return undefined; }
    const t = setTimeout(() => axiosInstance.get(API, { params: { pim: q } }).then(({ data }) => setHits(data.products || [])).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, picked]);
  const run = () => {
    setBusy(true); setErr(''); setR(null);
    axiosInstance.post(`${API}classify/`, { description: q, product_id: picked?.product_id })
      .then(({ data }) => setR(data)).catch((e) => setErr(e?.response?.data?.error || 'Classification failed.')).finally(() => setBusy(false));
  };
  return (
    <section className="mt-6">
      <div className="relative">
        <div className="flex gap-2">
          <input value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); }} onKeyDown={(e) => e.key === 'Enter' && q.trim() && run()}
            placeholder="Describe a product, or type a product name to pick it from the catalogue"
            className="flex-1 rounded-md border border-[var(--line)] bg-[var(--surface)] px-3 py-2" />
          <button type="button" disabled={busy || !q.trim()} onClick={run}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--ink)] px-4 py-2 font-medium text-white disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Classify
          </button>
        </div>
        {hits.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-md border border-[var(--line)] bg-[var(--surface)] text-sm shadow">
            {hits.map((h) => (
              <li key={`${h.product_id}-${h.v_material_en}`}>
                <button type="button" onClick={() => { setPicked(h); setQ(h.p_title); setHits([]); }}
                  className="w-full px-3 py-2 text-left hover:bg-[var(--paper)]">
                  <b>{h.p_title}</b> <span className="text-[var(--ink-3)]">{[h.v_material_en, h.v_color_en, h.v_sku].filter(Boolean).join(' · ')}</span>
                  {h.v_hs_code && <span className="ml-2 text-xs text-green-700">HS in catalogue: {h.v_hs_code}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {picked && <p className="mt-1 text-xs text-[var(--ink-3)]">Using the catalogue details of <b>{picked.p_title}</b> (material, dimensions, origin…).</p>}
      {err && <p className="mt-3 text-sm text-red-700">{err}</p>}
      {busy && <p className="mt-4 text-sm text-[var(--ink-3)]">Classifying and checking the codes against the official tables… (about 10-20 seconds)</p>}
      {r && (
        <div className="mt-5 space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <CodeCard title="EU (TARIC, 10 digits)" code={r.eu_cn10 || r.eu_cn8} description={r.eu_full_description || r.eu_cn10_description}
              valid={r.eu_ai_code_valid} corrected={r.eu_cn10_ai_original} />
            <CodeCard title="US (HTS, 10 digits)" code={r.us_hts} description={r.us_full_description || r.us_hts_description}
              valid={r.us_ai_code_valid} corrected={r.us_hts_ai_original} rate={r.us_duty_rate || ''} rateSource={r.us_duty_rate_source} special={r.us_special_rate} />
          </div>
          <div className="rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4 text-sm">
            <div><b>HS6 {r.primary_hs6}</b> · {r.primary_description} · confidence <b>{r.confidence}</b></div>
            {r.pim_product?.hs_code_in_pim && <div className="mt-1">HS code in our catalogue: <b>{r.pim_product.hs_code_in_pim}</b></div>}
            <p className="mt-2 text-[var(--ink-2)]">{r.reasoning}</p>
            {(r.alternative_codes || []).length > 0 && (
              <div className="mt-2 text-[var(--ink-2)]">Alternatives: {r.alternative_codes.map((a) => `${a.hs6} (${a.description})`).join(' · ')}</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

const TreeNode = ({ n, depth }) => {
  const [open, setOpen] = useState(false);
  const [kids, setKids] = useState(null);
  const toggle = () => {
    if (!open && kids === null) axiosInstance.get(API, { params: { children: n.id } }).then(({ data }) => setKids(data.nodes || []));
    setOpen(!open);
  };
  return (
    <li>
      <button type="button" onClick={toggle} className="flex w-full items-start gap-1.5 py-1 text-left text-sm hover:bg-[var(--paper)]"
        style={{ paddingLeft: `${depth * 16}px` }}>
        {open ? <ChevronDown size={14} className="mt-0.5 shrink-0" /> : <ChevronRight size={14} className="mt-0.5 shrink-0" />}
        <span className="num w-28 shrink-0 font-medium">{n.code}</span>
        <span className="flex-1 text-[var(--ink-2)]">{n.description}</span>
        {n.general_rate && <span className="shrink-0 text-xs text-[var(--ink-3)]">{n.general_rate}</span>}
      </button>
      {open && kids && <ul>{kids.map((k) => <TreeNode key={k.id} n={k} depth={depth + 1} />)}</ul>}
    </li>
  );
};

const Browser = () => {
  const [system, setSystem] = useState('EU');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      const params = q.trim() ? { q, system } : { sections: system };
      axiosInstance.get(API, { params }).then(({ data }) => setRows(data.nodes || [])).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, system]);
  return (
    <section className="mt-6">
      <div className="flex flex-wrap items-center gap-2">
        {SYSTEMS.map(([k, l]) => <Chip key={k} active={system === k} onClick={() => setSystem(k)}>{l}</Chip>)}
        <label className="ml-auto flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2.5 py-1.5">
          <Search size={15} className="text-[var(--ink-3)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code (9405.11) or words" className="w-64 bg-transparent text-sm outline-none" />
        </label>
      </div>
      {rows == null ? <div className="mt-4 h-40 animate-pulse rounded-lg bg-[var(--line-2)]" /> : (
        <ul className="mt-3 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-2">
          {rows.map((n) => <TreeNode key={n.id} n={n} depth={0} />)}
          {!rows.length && <li className="p-3 text-sm text-[var(--ink-3)]">Nothing found.</li>}
        </ul>
      )}
    </section>
  );
};

const CustomsV2 = () => {
  const [tab, setTab] = useState('classify');
  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <h1 className="text-[2.4rem] font-semibold leading-[1.1]">HS codes</h1>
        <p className="prose-body mt-2 max-w-3xl text-[var(--ink-2)]">
          HS codes for a product (EU TARIC and US HTS), checked against the official tables, with the US duty rate.
          For invoices and forwarders; never quote duties to a customer.
        </p>
        <div className="mt-5 flex gap-1 border-b border-[var(--line)] pb-3">
          <Chip active={tab === 'classify'} onClick={() => setTab('classify')}>Classify a product</Chip>
          <Chip active={tab === 'browse'} onClick={() => setTab('browse')}>Browse the codes</Chip>
        </div>
        {tab === 'classify' ? <Classifier /> : <Browser />}
      </div>
    </V2Shell>
  );
};

export default CustomsV2;
